import noop from 'lodash/noop';
import pWaitFor from 'p-wait-for';

import type {BackgroundTaskResult, Dependencies} from './types';

const DEFAULT_POLL_INTERVAL = 2000;
const DEFAULT_POLL_TIMEOUT = 1000 * 60 * 60;

export const poll = async (
  subscribe: Dependencies['subscribe'],
  taskId: string,
  options: {
    interval?: number;
    timeout?: number;
    onSuccess?: (result: BackgroundTaskResult<'SUCCESS'>) => void;
    onError?: (result?: BackgroundTaskResult<'FAILURE'>) => void;
  } = {
    interval: DEFAULT_POLL_INTERVAL,
    timeout: DEFAULT_POLL_TIMEOUT,
  },
) => {
  const {interval, timeout, onSuccess = noop, onError = noop} = options;

  return pWaitFor(
    async () => {
      const response = await subscribe(taskId).catch(e => {
        onError?.(e);
        return Promise.reject({data: {id: taskId, result: {type: `FAILURE`, error: e.message}}});
      });
      const {id, result} = response?.data || {result: {}};

      if (id && id !== taskId) {
        onError?.(new TaskIDMismatchError(taskId, id));
        return Promise.reject(new TaskIDMismatchError(taskId, id));
      }

      if (result.type === `PENDING`) {
        return false;
      }

      if (result.type === `SUCCESS`) {
        onSuccess?.(result);
        return pWaitFor.resolveWith(result);
      }

      if (result.type === `FAILURE`) {
        onError?.(result);
        return Promise.reject(result);
      }

      return false;
    },
    {
      interval,
      timeout,
    },
  );
};

class TaskIDMismatchError extends Error {
  constructor(taskId: string, id?: string) {
    super(`Task ID mismatch: ${id} !== ${taskId}`);
  }
}
